import React, { useEffect } from 'react'
import { Router } from '@reach/router'
import { ApplicationRoutes } from './app-routes'
import { ThemeProvider } from '@material-ui/core/styles'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import LuxonUtils from '@date-io/luxon'
import { useSelector, useDispatch } from 'react-redux'
import { actionAuthInit } from '../redux/auth/auth-actions'
import { CubitSnackbar } from 'common/cubit-snackbar/cubit-snackbar'
import { ApplicationRoutesPublic } from './app-routes-public'
import Spinner from 'common/spinner'
import { AppState } from '../redux/app/app-store'
import { useAuth, useUser } from '../redux/auth/auth-selectors'
import { setSessionKey } from 'utils'
import { createTheme } from '@material-ui/core/styles'
import AuthClient from 'clients/auth-client'

const isLoginCode = (value: string) => value.startsWith('lc_')

const appTheme = createTheme({
    palette: {
        primary: {
            light: '#0c7282',
            main: '#1a7e8e',
            dark: '#0c7282',
            contrastText: '#fff',
        },
        secondary: {
            light: '#0c7282',
            main: '#0c7282',
            dark: '#0c7282',
            contrastText: '#fff',
        },
    },
})

const App: React.FC = () => {
    const dispatch = useDispatch()
    const auth = useAuth()
    const user = useUser()

    useEffect(() => {
        const pathname = window.location.pathname
        if (pathname.includes('/sso-login/')) {
            // A session key (legacy links) or a login code to redeem first. A code that can't be redeemed, e.g.
            // spent by a reload, leaves the stored session as it is. Replace, so neither stays in back-button history.
            const token = pathname.split('/')[2]
            if (isLoginCode(token)) {
                AuthClient.redeemLoginCode(token)
                    .toPromise()
                    .then(
                        (r) => setSessionKey(r.sessionKey),
                        () => undefined,
                    )
                    .then(() => window.location.replace('/'))
                return
            }
            setSessionKey(token)
            window.location.replace('/')
        }
        dispatch(actionAuthInit())
    }, [dispatch])

    // set html element tag lang attribute
    const language = useSelector((state: AppState) => state.settings.language)
    document.documentElement.lang = language
    const userTags = user ? user.tags : []

    return (
        <ThemeProvider theme={appTheme}>
            <MuiPickersUtilsProvider utils={LuxonUtils} locale={language}>
                {auth.isInitialized ? (
                    auth.sessionKey ? (
                        <>
                            <Router style={{ height: '100%' }}>
                                {ApplicationRoutes.map((route, index) =>
                                    route({
                                        key: index,
                                        userTags,
                                    }),
                                )}
                            </Router>
                            <CubitSnackbar></CubitSnackbar>
                        </>
                    ) : (
                        <Router style={{ height: '100%' }}>
                            {ApplicationRoutesPublic.map((route, index) =>
                                route({
                                    key: index,
                                }),
                            )}
                        </Router>
                    )
                ) : (
                    <Spinner />
                )}
            </MuiPickersUtilsProvider>
        </ThemeProvider>
    )
}

export default App
